import React, { useCallback, useState, useEffect } from "react";
import { NodeViewWrapper } from "@tiptap/react";
import { Button, Typography, Space, message } from "antd";
import { DeleteOutlined, InfoCircleOutlined, DragOutlined, PaperClipOutlined, FilterOutlined } from "@ant-design/icons";
import { getContentBlockAttachmentConfig, canContentBlockGenerateAttachment, isContentBlockEditable, isContentBlockDataScopeAdjustable, getContentBlockFilterDimensions } from "../constants/contentBlockConstants";
import { attachmentService } from "../../../api/enhancedApi";
import "./ContentBlockNodeView.css";
import contentBlockRenderer from "../renderers/ContentBlockRenderer";
import { getEffectiveData } from "../utils/dataScopeResolver";
import DataScopeFilterModal from "../components/DataScopeFilterModal";

/**
 * 判断内容块类型是否需要滚动容器
 */
const needsScrollableContainer = (contentBlockType) => {
    const scrollableTypes = ["scheduleFlightList", "scheduleFlightListWithComparison"];
    return scrollableTypes.includes(contentBlockType);
};

/**
 * ContentBlockNodeView 组件 - V2.5 增强版
 * 支持动态数据渲染和双向绑定
 * V2.5: 支持序号显示，与侧边栏序号对应
 * V2.6: 支持生成附件功能
 */
const ContentBlockNodeView = ({ node, deleteNode, editor }) => {
    const { contentBlockType, contentBlockLabel, status, contentBlockId, instanceId, isInline } = node.attrs;

    // 检测是否为行内内容块
    const isInlineBlock = isInline || node.type.name === "inlineContentBlock";
    const [filterModalOpen, setFilterModalOpen] = useState(false);

    // V2.5 新增：计算当前实例的序号
    const getInstanceIndex = useCallback(() => {
        if (!instanceId || !contentBlockType) return null;

        // 获取编辑器中所有同类型的实例，按文档顺序排列
        const sameTypeInstances = [];
        editor.state.doc.descendants((node, pos) => {
            if ((node.type.name === "contentBlock" || node.type.name === "inlineContentBlock") && node.attrs.contentBlockType === contentBlockType && node.attrs.instanceId) {
                sameTypeInstances.push({
                    instanceId: node.attrs.instanceId,
                    pos: pos,
                });
            }
        });

        // 按位置排序
        sameTypeInstances.sort((a, b) => a.pos - b.pos);

        // 找到当前实例的索引
        const currentIndex = sameTypeInstances.findIndex((instance) => instance.instanceId === instanceId);
        return currentIndex >= 0 ? currentIndex + 1 : null;
    }, [editor, instanceId, contentBlockType]);

    // 获取当前实例的序号
    const instanceIndex = getInstanceIndex();

    // 从编辑器存储中获取业务数据
    // 查找策略：1. 按 id 匹配 → 2. 按 type 匹配 → 3. 顶层数据兜底
    const getBusinessDataFromStorage = useCallback(
        (contentBlockId) => {
            const businessContext = editor.storage?.businessContext;
            if (!businessContext) return null;

            const { contentBlocks = [] } = businessContext;

            // 策略1: 按 id 精确匹配
            let block = contentBlocks.find((b) => b.id === contentBlockId);
            if (block?.data) return block.data;

            // 策略2: 按 type 匹配
            block = contentBlocks.find((b) => b.type === contentBlockType);
            if (block?.data) return block.data;

            // 策略3: 顶层数据兜底（支持简化的数据结构）
            switch (contentBlockType) {
                case "operationFlightList":
                    if (businessContext.flightOperations) return businessContext.flightOperations;
                    if (businessContext.operations) return businessContext.operations;
                    if (businessContext.rawData?.flights) return businessContext.rawData.flights;
                    if (businessContext.dataPayload) return businessContext.dataPayload;
                    return null;

                case "flightWithPredecessor":
                    if (businessContext.flightsWithPredecessor) return businessContext.flightsWithPredecessor;
                    if (businessContext.flightOperations) return businessContext.flightOperations;
                    if (businessContext.rawData?.flights) return businessContext.rawData.flights;
                    if (businessContext.dataPayload) return businessContext.dataPayload;
                    return null;

                case "scheduleFlightList":
                case "scheduleFlightListWithComparison":
                    if (businessContext.flights) return businessContext.flights;
                    if (businessContext.comparisonData) return businessContext.comparisonData;
                    if (businessContext.schedules) return { flights: businessContext.schedules };
                    return null;

                case "scheduleInfo":
                    return businessContext.schedule || businessContext.schedule_info || null;

                default:
                    return null;
            }
        },
        [editor, contentBlockType],
    );

    // 专门处理 flightWithPredecessor 的异步数据获取
    const [enhancedFlightData, setEnhancedFlightData] = useState(null);
    const [isLoadingPredecessor, setIsLoadingPredecessor] = useState(false);

    useEffect(() => {
        if (contentBlockType === "flightWithPredecessor") {
            const businessData = getBusinessDataFromStorage(contentBlockId);
            if (businessData && !isLoadingPredecessor) {
                setIsLoadingPredecessor(true);

                // 直接使用业务数据，不做任何处理
                // 数据应该在 UnifiedTransactionEmailDrawer 中已经预处理好了
                setEnhancedFlightData(businessData);
                setIsLoadingPredecessor(false);
            }
        }
    }, [contentBlockType, contentBlockId, getBusinessDataFromStorage, isLoadingPredecessor]);

    // 获取最终的业务数据（对于 flightWithPredecessor 使用增强数据）
    const getFinalBusinessData = useCallback(
        (contentBlockId) => {
            if (contentBlockType === "flightWithPredecessor") {
                return enhancedFlightData || getBusinessDataFromStorage(contentBlockId);
            }
            return getBusinessDataFromStorage(contentBlockId);
        },
        [contentBlockType, enhancedFlightData, getBusinessDataFromStorage],
    );

    const getCurrentInstance = useCallback(() => {
        return editor.storage?.contentBlockInstances?.find((instance) => instance.instanceId === instanceId) || null;
    }, [editor, instanceId]);

    const getEffectiveBusinessData = useCallback(
        (rawData) => {
            return getEffectiveData(getCurrentInstance(), rawData);
        },
        [getCurrentInstance],
    );

    // 渲染序号标识
    const renderInstanceIndex = useCallback(() => {
        if (!instanceIndex) return null;

        return (
            <span
                className="content-block-instance-index"
                style={{
                    backgroundColor: "#1890ff",
                    color: "white",
                    borderRadius: "50%",
                    width: "18px",
                    height: "18px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    fontWeight: "bold",
                    marginRight: "6px",
                    flexShrink: 0,
                }}
                title={`${contentBlockType} 实例 ${instanceIndex}`}
            >
                {instanceIndex}
            </span>
        );
    }, [instanceIndex, contentBlockType]);

    // 所有渲染函数已迁移到统一渲染服务 ContentBlockRenderer

    // 渲染行内内容块的实际内容 - 使用统一渲染服务
    const renderInlineContent = useCallback(() => {
        const businessData = getFinalBusinessData(contentBlockId);
        if (!businessData) {
            if (contentBlockType === "flightWithPredecessor" && isLoadingPredecessor) {
                return "正在加载航班数据...";
            }
            return contentBlockLabel || contentBlockType;
        }

        try {
            const currentInstance = getCurrentInstance();
            const effectiveBusinessData = getEffectiveData(currentInstance, businessData);
            // 获取完整的业务上下文，并包含 contentBlockId 用于区分不同的内容块
            const businessContext = editor.storage?.businessContext || {};
            const options = {
                ...businessContext,
                contentBlockId: contentBlockId, // 关键：传递 contentBlockId 用于区分显示不同数据
                contentBlockInstance: currentInstance,
            };

            // 使用统一渲染服务进行行内文本渲染
            const htmlContent = contentBlockRenderer.renderInlineText(contentBlockType, effectiveBusinessData, options);

            // 如果返回的是HTML内容，返回一个包含HTML的React元素
            if (typeof htmlContent === "string" && htmlContent.includes("<")) {
                return <span dangerouslySetInnerHTML={{ __html: htmlContent }} />;
            }

            return htmlContent;
        } catch (error) {
            console.error("行内内容块渲染错误:", error, { contentBlockType, businessData });
            return contentBlockLabel || contentBlockType;
        }
    }, [contentBlockId, contentBlockType, contentBlockLabel, getFinalBusinessData, getCurrentInstance, editor, isLoadingPredecessor]);

    // 渲染实际内容 - 使用统一渲染服务
    const renderActualContent = useCallback(() => {
        const businessData = getFinalBusinessData(contentBlockId);
        if (!businessData) {
            if (contentBlockType === "flightWithPredecessor" && isLoadingPredecessor) {
                return (
                    <div style={{ padding: "12px" }}>
                        <p style={{ color: "#1890ff", margin: 0 }}>正在加载前序航班数据...</p>
                    </div>
                );
            }
            return (
                <div style={{ padding: "12px" }}>
                    <p style={{ color: "#999", margin: 0 }}>无法获取业务数据</p>
                </div>
            );
        }

        try {
            const currentInstance = getCurrentInstance();
            const effectiveBusinessData = getEffectiveData(currentInstance, businessData);
            const businessContext = editor.storage?.businessContext || {};
            // 使用统一渲染服务进行编辑器模式渲染
            const result = contentBlockRenderer.renderForEditor(contentBlockType, effectiveBusinessData, {
                ...businessContext,
                contentBlockInstance: currentInstance,
            });

            // 现在所有渲染器都返回HTML字符串，直接使用dangerouslySetInnerHTML
            if (typeof result === "string") {
                return <div dangerouslySetInnerHTML={{ __html: result }} />;
            }

            // 如果渲染器返回React组件结构（向后兼容），转换为JSX
            if (result && typeof result === "object" && result.type) {
                return convertToJSX(result);
            }

            return result;
        } catch (error) {
            console.error("内容块渲染错误:", error, { contentBlockType, businessData });
            return (
                <div style={{ padding: "12px" }}>
                    <p style={{ color: "#ff4d4f", margin: 0 }}>渲染错误: {error.message}</p>
                </div>
            );
        }
    }, [contentBlockId, contentBlockType, getFinalBusinessData, getCurrentInstance, isLoadingPredecessor, editor]);

    // 转换React组件结构为JSX的辅助函数
    const convertToJSX = (componentStructure) => {
        if (!componentStructure || typeof componentStructure !== "object") {
            return componentStructure;
        }

        const { type, props } = componentStructure;

        if (!type) {
            return componentStructure;
        }

        const TagName = type;
        const { children, ...otherProps } = props || {};

        if (Array.isArray(children)) {
            return React.createElement(TagName, otherProps, ...children.map((child) => convertToJSX(child)));
        } else if (children !== undefined) {
            return React.createElement(TagName, otherProps, convertToJSX(children));
        } else {
            return React.createElement(TagName, otherProps);
        }
    };

    const handleDelete = (e) => {
        e.stopPropagation(); // 防止触发拖拽
        e.preventDefault();
        deleteNode();
    };

    // 生成附件功能
    const [generatingAttachment, setGeneratingAttachment] = useState(false);

    const handleGenerateAttachment = useCallback(
        async (e) => {
            e.stopPropagation();
            e.preventDefault();

            console.log("[handleGenerateAttachment] 开始生成附件:", {
                contentBlockType,
                contentBlockId,
                canGenerate: canContentBlockGenerateAttachment(contentBlockType)
            });

            if (!canContentBlockGenerateAttachment(contentBlockType)) {
                message.warning(`内容块类型 ${contentBlockType} 不支持生成附件`);
                return;
            }

            const attachmentConfig = getContentBlockAttachmentConfig(contentBlockType);
            const businessData = getFinalBusinessData(contentBlockId);
            const effectiveBusinessData = getEffectiveBusinessData(businessData);

            if (!businessData) {
                message.warning("缺少业务数据，无法生成附件");
                return;
            }

            setGeneratingAttachment(true);

            try {
                // 根据内容块类型构造适合的数据结构
                let contentBlockData = effectiveBusinessData;

                if (contentBlockType === "scheduleFlightList" && Array.isArray(effectiveBusinessData)) {
                    // 对于 scheduleFlightList 类型，需要构造包含额外信息的对象
                    const contentBlock = editor.storage?.businessContext?.contentBlocks?.find((block) => block.id === contentBlockId);
                    contentBlockData = {
                        flights: effectiveBusinessData,
                        scheduleFlights: effectiveBusinessData,
                        schedule_info: contentBlock?.schedule_info || {},
                        schedule: contentBlock?.schedule_info || {},
                    };
                } else if (contentBlockType === "operationFlightList" && Array.isArray(effectiveBusinessData)) {
                    // 对于 operationFlightList 类型，需要构造包含额外信息的对象
                    const contentBlock = editor.storage?.businessContext?.contentBlocks?.find((block) => block.id === contentBlockId);
                    contentBlockData = {
                        flights: effectiveBusinessData,
                        flightOperations: effectiveBusinessData,
                        allFlights: effectiveBusinessData,
                        transaction_info: contentBlock?.transaction_info || {},
                        business_source: contentBlock?.business_source || {},
                    };
                }

                const requestData = {
                    content_block_type: contentBlockType,
                    content_block_data: contentBlockData,
                    export_service: attachmentConfig.exportService,
                    filename: contentBlockLabel ? `${contentBlockLabel}.xlsx` : attachmentConfig.defaultFilename,
                };

                // 添加调试日志
                console.log("[ContentBlockNodeView] 生成附件请求数据:", {
                    contentBlockType,
                    contentBlockId,
                    originalBusinessData: businessData,
                    effectiveBusinessData,
                    processedContentBlockData: contentBlockData,
                    requestData,
                });

                const result = await attachmentService.generateAttachmentFromContentBlock(requestData);

                message.success(`附件 "${result.filename}" 生成成功`);

                // 通知父组件附件已生成
                const onAttachmentGenerated = editor.storage?.onAttachmentGenerated;
                if (onAttachmentGenerated) {
                    onAttachmentGenerated(result);
                }
            } catch (error) {
                console.error("生成附件失败:", error);
                message.error(`生成附件失败: ${error.message}`);
            } finally {
                setGeneratingAttachment(false);
            }
        },
        [contentBlockType, contentBlockId, getFinalBusinessData, getEffectiveBusinessData, editor],
    );

    const handleOpenFilterModal = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        setFilterModalOpen(true);
    }, []);

    const handleApplyFilter = useCallback(
        (filterState) => {
            const updateInstanceFilter = editor.storage?.updateInstanceFilter;
            if (!updateInstanceFilter) {
                message.error("无法更新内容块筛选状态");
                return;
            }

            updateInstanceFilter(instanceId, filterState, editor);
            setFilterModalOpen(false);
        },
        [editor, instanceId],
    );

    const renderDataScopeFilterButton = useCallback(() => {
        if (isInlineBlock || !isContentBlockDataScopeAdjustable(contentBlockType)) {
            return null;
        }

        const currentInstance = getCurrentInstance();
        const hasActiveFilter = Array.isArray(currentInstance?.filterState?.selectedIds);

        return (
            <Button
                type={hasActiveFilter ? "primary" : "text"}
                size="small"
                icon={<FilterOutlined />}
                onClick={handleOpenFilterModal}
                onMouseDown={(e) => e.stopPropagation()}
                title="调整数据范围"
                style={{
                    color: hasActiveFilter ? undefined : "#1890ff",
                    cursor: "pointer",
                    marginRight: "4px",
                }}
            />
        );
    }, [contentBlockType, getCurrentInstance, handleOpenFilterModal, isInlineBlock]);

    // 渲染生成附件按钮
    const renderGenerateAttachmentButton = useCallback(() => {
        if (!canContentBlockGenerateAttachment(contentBlockType)) {
            return null;
        }

        const attachmentConfig = getContentBlockAttachmentConfig(contentBlockType);

        return (
            <Button
                type="text"
                size="small"
                icon={<PaperClipOutlined />}
                loading={generatingAttachment}
                onClick={handleGenerateAttachment}
                onMouseDown={(e) => e.stopPropagation()}
                title={attachmentConfig.buttonText || "生成附件"}
                style={{
                    color: "#1890ff",
                    cursor: "pointer",
                    marginRight: "4px",
                }}
            />
        );
    }, [contentBlockType, generatingAttachment, handleGenerateAttachment]);

    // V2 模式：检查是否能获取到业务数据
    const businessData = getFinalBusinessData(contentBlockId);
    const hasBusinessData = !!businessData;

    // V2 模式：如果有业务数据，渲染实际内容（不管status是什么）
    // V1 模式：只有status为filled时才渲染实际内容
    if (hasBusinessData && (status === "filled" || editor.storage.businessContext)) {
        // 行内内容块的紧凑样式
        if (isInlineBlock) {
            return (
                <NodeViewWrapper className="react-renderer node-inlineContentBlock" data-drag-handle as="span">
                    <span
                        className="inline-content-block-wrapper"
                        draggable="true"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            border: canContentBlockGenerateAttachment(contentBlockType) ? "1px solid #1890ff" : "1px solid #52c41a",
                            borderRadius: "4px",
                            backgroundColor: canContentBlockGenerateAttachment(contentBlockType) ? "#e6f7ff" : "#f6ffed",
                            padding: "2px 6px",
                            margin: "0 2px",
                            fontSize: "inherit",
                            color: canContentBlockGenerateAttachment(contentBlockType) ? "#1890ff" : "#52c41a",
                            userSelect: "none",
                            cursor: "grab",
                        }}
                        title={`${contentBlockLabel || contentBlockType} (行内内容块 - 可拖拽)${canContentBlockGenerateAttachment(contentBlockType) ? " - 支持生成附件" : ""}`}
                    >
                        {renderInstanceIndex()}
                        <DragOutlined style={{ fontSize: "10px", marginRight: "4px", cursor: "grab", opacity: 0.6 }} />
                        <span
                            contentEditable={true}
                            suppressContentEditableWarning={true}
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{
                                fontWeight: "bold",
                                textDecoration: isInlineBlock ? "underline" : "none",
                                outline: "none",
                                cursor: "text",
                            }}
                        >
                            {renderInlineContent()}
                        </span>
                        {/* 行内版本的生成附件按钮 */}
                        {canContentBlockGenerateAttachment(contentBlockType) && (
                            <Button
                                title={getContentBlockAttachmentConfig(contentBlockType)?.buttonText || "生成附件"}
                                type="text"
                                size="small"
                                icon={<PaperClipOutlined />}
                                loading={generatingAttachment}
                                onClick={handleGenerateAttachment}
                                onMouseDown={(e) => e.stopPropagation()}
                                style={{
                                    color: "#1890ff",
                                    cursor: "pointer",
                                    padding: "0",
                                    marginLeft: "4px",
                                    fontSize: "12px",
                                    minWidth: "auto",
                                    height: "auto",
                                }}
                            />
                        )}
                        <Button
                            title="删除此内容块"
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={handleDelete}
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{
                                color: "#999",
                                cursor: "pointer",
                                padding: "0",
                                marginLeft: "4px",
                                fontSize: "10px",
                                minWidth: "auto",
                                height: "auto",
                            }}
                        />
                    </span>
                </NodeViewWrapper>
            );
        }

        // 块级内容块的原有样式
        return (
            <NodeViewWrapper className="react-renderer node-contentBlock">
                <div className="content-block-node-wrapper">
                    <div className="content-block-container content-block-filled" style={{ border: "2px solid #52c41a", borderRadius: "6px", backgroundColor: "#fff", margin: "8px 0" }}>
                        <div
                            className="content-block-header content-block-drag-area"
                            data-drag-handle
                            style={{
                                padding: "8px 12px",
                                borderBottom: "1px solid #b7eb8f",
                                backgroundColor: "#f6ffed",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                cursor: "grab",
                            }}
                        >
                            <Space size="small" style={{ flex: 1, cursor: "grab" }}>
                                <div className="content-block-drag-handle" title="拖拽移动此内容块">
                                    <DragOutlined style={{ color: "#52c41a", cursor: "grab" }} />
                                </div>
                                {renderInstanceIndex()}
                                <span className="content-block-icon" style={{ color: "#52c41a" }}>
                                    📅
                                </span>
                                <Typography.Text style={{ color: "#52c41a", fontSize: "13px", cursor: "grab", userSelect: "none" }}>
                                    <strong>{contentBlockLabel || contentBlockType}</strong>
                                </Typography.Text>
                            </Space>
                            <Space size="small">
                                {renderDataScopeFilterButton()}
                                {renderGenerateAttachmentButton()}
                                <Button title="删除此内容块" type="text" size="small" icon={<DeleteOutlined />} onClick={handleDelete} onMouseDown={(e) => e.stopPropagation()} style={{ color: "#999", cursor: "pointer" }} />
                            </Space>
                        </div>
                        <div
                            className="content-block-body"
                            contentEditable={isContentBlockEditable(contentBlockType)}
                            suppressContentEditableWarning={true}
                            onMouseDown={(e) => {
                                // 如果可编辑，阻止拖拽事件传播
                                if (isContentBlockEditable(contentBlockType)) {
                                    e.stopPropagation();
                                }
                            }}
                            style={{
                                pointerEvents: "auto",
                                userSelect: "auto",
                                outline: isContentBlockEditable(contentBlockType) ? "none" : undefined,
                                ...(needsScrollableContainer(contentBlockType) && {
                                    maxHeight: "300px",
                                    overflowY: "auto",
                                    border: "1px solid #f0f0f0",
                                    borderRadius: "4px",
                                }),
                            }}
                        >
                            {renderActualContent()}
                        </div>
                    </div>
                    <DataScopeFilterModal
                        open={filterModalOpen}
                        instance={getCurrentInstance()}
                        rawData={businessData}
                        filterDimensions={getContentBlockFilterDimensions(contentBlockType)}
                        onApply={handleApplyFilter}
                        onCancel={() => setFilterModalOpen(false)}
                    />
                </div>
            </NodeViewWrapper>
        );
    }

    // V1 模式或 pending 状态：渲染占位符
    // 行内内容块的紧凑占位符样式
    if (isInlineBlock) {
        return (
            <NodeViewWrapper className="react-renderer node-inlineContentBlock" data-drag-handle as="span">
                <span
                    className="inline-content-block-wrapper pending"
                    draggable="true"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        border: canContentBlockGenerateAttachment(contentBlockType) ? "1px dashed #91d5ff" : "1px dashed #d9d9d9",
                        borderRadius: "4px",
                        backgroundColor: canContentBlockGenerateAttachment(contentBlockType) ? "#f0f8ff" : "#fafafa",
                        padding: "2px 6px",
                        margin: "0 2px",
                        fontSize: "inherit",
                        color: canContentBlockGenerateAttachment(contentBlockType) ? "#69c0ff" : "#999",
                        userSelect: "none",
                        cursor: "grab",
                    }}
                    title={`${contentBlockLabel || contentBlockType} (行内内容块 - 可拖拽)${canContentBlockGenerateAttachment(contentBlockType) ? " - 支持生成附件" : ""}`}
                >
                    {renderInstanceIndex()}
                    <DragOutlined style={{ fontSize: "10px", marginRight: "4px", cursor: "grab", opacity: 0.6 }} />
                    <span
                        contentEditable={true}
                        suppressContentEditableWarning={true}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{
                            fontWeight: "bold",
                            textDecoration: isInlineBlock ? "underline" : "none",
                            outline: "none",
                            cursor: "text",
                        }}
                    >
                        {renderInlineContent()}
                    </span>
                    {/* 行内版本的生成附件按钮（pending状态） */}
                    {canContentBlockGenerateAttachment(contentBlockType) && (
                        <Button
                            title={getContentBlockAttachmentConfig(contentBlockType)?.buttonText || "生成附件"}
                            type="text"
                            size="small"
                            icon={<PaperClipOutlined />}
                            loading={generatingAttachment}
                            onClick={handleGenerateAttachment}
                            onMouseDown={(e) => e.stopPropagation()}
                            disabled={!hasBusinessData} // pending状态下禁用，直到有业务数据
                            style={{
                                color: hasBusinessData ? "#1890ff" : "#ccc",
                                cursor: hasBusinessData ? "pointer" : "not-allowed",
                                padding: "0",
                                marginLeft: "4px",
                                fontSize: "12px",
                                minWidth: "auto",
                                height: "auto",
                            }}
                        />
                    )}
                    <Button
                        title="删除此内容块"
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={handleDelete}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{
                            color: "#999",
                            cursor: "pointer",
                            padding: "0",
                            marginLeft: "4px",
                            fontSize: "10px",
                            minWidth: "auto",
                            height: "auto",
                        }}
                    />
                </span>
            </NodeViewWrapper>
        );
    }

    // 块级内容块的原有占位符样式
    return (
        <NodeViewWrapper className="react-renderer node-contentBlock">
            <div className="content-block-node-wrapper">
                <div className="content-block-container content-block-pending" style={{ border: "2px dashed #d9d9d9", borderRadius: "6px", backgroundColor: "#fafafa", margin: "8px 0" }}>
                    <div
                        className="content-block-header content-block-drag-area"
                        data-drag-handle
                        style={{
                            padding: "8px 12px",
                            borderBottom: "1px solid #e8e8e8",
                            backgroundColor: "#f5f5f5",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            cursor: "grab",
                        }}
                    >
                        <Space size="small" style={{ flex: 1, cursor: "grab" }}>
                            <div className="content-block-drag-handle" title="拖拽移动此内容块">
                                <DragOutlined style={{ color: "#999", cursor: "grab" }} />
                            </div>
                            {renderInstanceIndex()}
                            <span className="content-block-icon" style={{ color: "#999" }}>
                                📝
                            </span>
                            <Typography.Text style={{ color: "#666", fontSize: "13px", cursor: "grab", userSelect: "none" }}>
                                <strong>{contentBlockLabel || contentBlockType}</strong>
                            </Typography.Text>
                        </Space>
                        <Button title="删除此内容块" type="text" size="small" icon={<DeleteOutlined />} onClick={handleDelete} onMouseDown={(e) => e.stopPropagation()} style={{ color: "#999", cursor: "pointer" }} />
                    </div>
                    <div 
                        className="content-block-body" 
                        contentEditable={isContentBlockEditable(contentBlockType)}
                        suppressContentEditableWarning={true}
                        onMouseDown={(e) => {
                            // 如果可编辑，阻止拖拽事件传播
                            if (isContentBlockEditable(contentBlockType)) {
                                e.stopPropagation();
                            }
                        }}
                        style={{ 
                            pointerEvents: "auto", 
                            userSelect: "auto",
                            outline: isContentBlockEditable(contentBlockType) ? "none" : undefined,
                        }}
                    >
                        <div style={{ padding: "12px", textAlign: "center", color: "#999" }}>
                            <InfoCircleOutlined style={{ marginRight: 4 }} />
                            等待数据加载...
                        </div>
                    </div>
                </div>
            </div>
        </NodeViewWrapper>
    );
};

export default ContentBlockNodeView;
